// src/components/PredatorChase.jsx
import React, { useRef, useState, useEffect } from 'react';
import '../css/chase.css';

/**
 * Props:
 *  - imgSrc: string
 *  - width?: number
 *  - speed?: number
 *  - active?: boolean
 *  - targetRef?: React.RefObject<HTMLElement>
 *  - catchRadius?: number
 *  - onCatch?: (fishElement: HTMLElement) => void
 *  - onClick?: () => void
 *  - highlight?: boolean      // whether to draw a ring around predator
 *  - theme?: 'light' | 'dark' // optional, in case you want a different ring color
 */
export default function PredatorChase({
  imgSrc,
  width = 80,
  speed = 1.5,
  active = false,
  targetRef,
  catchRadius = 30,
  onCatch,
  onClick,
  highlight = false,
  theme = 'light',
  ...props
}) {
  const el = useRef(null);
  const [pos, setPos] = useState({ x: -200, y: 0 });
  const [dir, setDir] = useState(1);
  const [scale, setScale] = useState(1);

  useEffect(() => {
    if (!active || !targetRef?.current) return;
    let frameId;

    function chaseOrPatrol() {
      const fishElems = Array.from(
        targetRef.current.querySelectorAll('.prey-fish')
      );

      if (fishElems.length > 0) {
        // ─── CHASE MODE ───
        const pRect = el.current.getBoundingClientRect();
        const predatorCenter = {
          x: pRect.left + pRect.width / 2,
          y: pRect.top + pRect.height / 2,
        };

        let nearestDist = Infinity;
        let nearestDelta = { dx: 0, dy: 0 };
        let nearestEl = null;

        fishElems.forEach((f) => {
          const r = f.getBoundingClientRect();
          const dx = r.left + r.width / 2 - predatorCenter.x;
          const dy = r.top + r.height / 2 - predatorCenter.y;
          const d2 = dx * dx + dy * dy;
          if (d2 < nearestDist) {
            nearestDist = d2;
            nearestDelta = { dx, dy };
            nearestEl = f;
          }
        });

        const dist = Math.sqrt(nearestDist) || 1;

        if (dist < catchRadius && nearestEl) {
          nearestEl.remove();
          onCatch && onCatch(nearestEl);
          setScale((s) => s + 0.1);
          frameId = requestAnimationFrame(chaseOrPatrol);
          return;
        }

        setPos((p) => ({
          x: p.x + (nearestDelta.dx / dist) * speed,
          y: p.y + (nearestDelta.dy / dist) * speed,
        }));
        setDir(nearestDelta.dx >= 0 ? 1 : -1);
      } else {
        // ─── PATROL MODE ───
        const spriteW = width * scale;
        const maxX = window.innerWidth - spriteW;
        setPos((p) => {
          let newX = p.x + dir * speed;
          if (newX >= maxX && dir > 0) {
            newX = maxX;
            setDir(-1);
          }
          if (newX <= 0 && dir < 0) {
            newX = 0;
            setDir(1);
          }
          return { x: newX, y: p.y };
        });
      }

      frameId = requestAnimationFrame(chaseOrPatrol);
    }

    frameId = requestAnimationFrame(chaseOrPatrol);
    return () => cancelAnimationFrame(frameId);
  }, [active, speed, targetRef, catchRadius, onCatch, dir, scale, width]);

  // Only toggle the ring classes when highlighted; no opacity change
  const ringClasses = highlight
    ? 'ring-4 ring-yellow-300 rounded-full scale-110'
    : '';

  return (
    <img
      ref={el}
      src={imgSrc}
      alt="Predator"
      onClick={onClick}
      className={`predator inline-block ${ringClasses}`}
      style={{
        width: `${width}px`,
        // Always fully opaque—no opacity: 0.4 here.
        opacity: 1,
        transform: `
          translate(${pos.x}px, ${pos.y}px)
          scale(${scale})
          ${dir < 0 ? 'rotateY(180deg)' : ''}
        `,
      }}
      {...props}
    />
  );
}
